import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Eraser, Pen } from 'lucide-react';

/**
 * Canvas-based signature pad. Captures pointer/touch strokes and exports a PNG data URL.
 * Props:
 *   - onChange(dataUrl|null): called every time the signature changes
 *   - height (default 160)
 *   - testId
 *   - disabled
 */
export const SignaturePad = ({ onChange, height = 160, testId, disabled = false, initialDataUrl }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const drawingRef = useRef(false);
    const lastPointRef = useRef(null);
    const [empty, setEmpty] = useState(true);

    // Setup canvas DPR & resize
    const setupCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = container.getBoundingClientRect();
        canvas.width = Math.max(1, Math.floor(rect.width * dpr));
        canvas.height = Math.max(1, Math.floor(height * dpr));
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${height}px`;
        const ctx = canvas.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, rect.width, height);
        ctx.strokeStyle = '#0A0A0A';
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    }, [height]);

    useEffect(() => {
        setupCanvas();
        const ro = new ResizeObserver(setupCanvas);
        if (containerRef.current) ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [setupCanvas]);

    // Load initial signature if provided
    useEffect(() => {
        if (!initialDataUrl) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const img = new window.Image();
        img.onload = () => {
            const rect = containerRef.current.getBoundingClientRect();
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, rect.width, height);
            setEmpty(false);
        };
        img.src = initialDataUrl;
    }, [initialDataUrl, height]);

    const getPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const point = e.touches ? e.touches[0] : e;
        return {
            x: point.clientX - rect.left,
            y: point.clientY - rect.top,
        };
    };

    const startDraw = (e) => {
        if (disabled) return;
        e.preventDefault();
        drawingRef.current = true;
        lastPointRef.current = getPos(e);
    };

    const moveDraw = (e) => {
        if (!drawingRef.current || disabled) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const pt = getPos(e);
        const last = lastPointRef.current;
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.stroke();
        lastPointRef.current = pt;
        if (empty) setEmpty(false);
    };

    const endDraw = () => {
        if (!drawingRef.current) return;
        drawingRef.current = false;
        if (onChange) {
            const dataUrl = canvasRef.current.toDataURL('image/png');
            onChange(dataUrl);
        }
    };

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = containerRef.current.getBoundingClientRect();
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, rect.width, height);
        setEmpty(true);
        if (onChange) onChange(null);
    };

    return (
        <div className="space-y-2" data-testid={testId}>
            <div
                ref={containerRef}
                className="relative border-2 border-dashed border-[#E4E4E7] rounded-sm bg-white overflow-hidden touch-none"
                style={{ height }}
            >
                <canvas
                    ref={canvasRef}
                    className="block w-full h-full cursor-crosshair"
                    onMouseDown={startDraw}
                    onMouseMove={moveDraw}
                    onMouseUp={endDraw}
                    onMouseLeave={endDraw}
                    onTouchStart={startDraw}
                    onTouchMove={moveDraw}
                    onTouchEnd={endDraw}
                    data-testid={testId ? `${testId}-canvas` : undefined}
                />
                {empty && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-[#A1A1AA] text-sm">
                        <Pen className="w-4 h-4 mr-2" /> Signez ici
                    </div>
                )}
            </div>
            <div className="flex justify-end">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clear}
                    disabled={disabled || empty}
                    className="text-[#52525B] hover:text-[#DC2626]"
                    data-testid={testId ? `${testId}-clear` : undefined}
                >
                    <Eraser className="w-3.5 h-3.5 mr-1" /> Effacer
                </Button>
            </div>
        </div>
    );
};
